import { useEffect, useRef } from 'react';
import { MessageView, type Message } from './Message';

interface MessageListProps {
  messages: Message[];
}

export function MessageList({ messages }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Whether the user is currently following the stream. Starts true so
  // the first answer scrolls into view automatically. Flips off when the
  // user actively scrolls up (wheel / touch / page-up etc.); flips back
  // on when they send a new message or actively scroll back to the
  // bottom.
  //
  // Crucially we only update this from REAL user-input events — never
  // from the `scroll` event, because the `scroll` event also fires for
  // our own programmatic scrolls and races with content growth. Wheel /
  // touch / keyboard signal *intent*, which is what we actually care
  // about.
  const stickToBottomRef = useRef(true);

  // Last-seen message count — when it grows, a NEW message was
  // appended (user pressed send, or the reducer added the assistant
  // placeholder). A new message always re-pins to bottom — sending a
  // new question is an unambiguous "show me the bottom" signal.
  const lastLengthRef = useRef(messages.length);

  // Detect user-initiated scrolls. After any wheel/touch/keyboard input,
  // re-read the scroll position once the browser has applied it and
  // decide whether we're still at the bottom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const recheck = () => {
      requestAnimationFrame(() => {
        // Generous threshold: even at "near the bottom" we keep
        // following. Only a clear scroll-up should disengage.
        const distanceFromBottom =
          el.scrollHeight - el.scrollTop - el.clientHeight;
        stickToBottomRef.current = distanceFromBottom <= 16;
      });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Keys that move the scroll container.
      if (
        e.key === 'PageUp' ||
        e.key === 'PageDown' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === ' '
      ) {
        recheck();
      }
    };
    el.addEventListener('wheel', recheck, { passive: true });
    el.addEventListener('touchmove', recheck, { passive: true });
    el.addEventListener('keydown', onKeyDown);
    return () => {
      el.removeEventListener('wheel', recheck);
      el.removeEventListener('touchmove', recheck);
      el.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Watch every direct child of the container with a ResizeObserver,
  // plus the container itself with a MutationObserver for newly added
  // children. Any size change while we're following the stream pins us
  // back to the bottom. This handles streaming deltas, the SourcesList
  // rendering on `done`, late-rendering markdown, etc.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const pin = () => {
      if (!stickToBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
    };
    const ro = new ResizeObserver(pin);
    for (const child of Array.from(el.children)) ro.observe(child);
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of Array.from(m.addedNodes)) {
          if (n instanceof HTMLElement) ro.observe(n);
        }
      }
      pin();
    });
    mo.observe(el, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  // On every new `messages` array, decide whether to force-repin. A new
  // message appended (length increased) is always a repin signal —
  // sending a new question while reading is unambiguously "follow the
  // new answer", even if we'd previously scrolled up.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (messages.length > lastLengthRef.current) {
      stickToBottomRef.current = true;
    }
    lastLengthRef.current = messages.length;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-4">
      {messages.map((m) => (
        <MessageView key={m.id} {...m} />
      ))}
    </div>
  );
}
